/**
 * Nueva contraseña con el **código** del email (contrato §1). El correo trae un código
 * para pegar acá, no un enlace: todos los textos hablan de código.
 * Cambiar la contraseña **cierra todas las sesiones abiertas** del usuario: si el cambio
 * fue por sospecha, sirve de algo.
 */
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { KeyRound } from "lucide-react";
import { ApiError, http } from "../../lib/api.js";
import { Button } from "../../components/ui/Button.js";
import { Input } from "../../components/ui/Input.js";
import { AuthShell } from "./AuthShell.js";

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState(params.get("token") ?? "");
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mismatch = repeat.length > 0 && password !== repeat;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (mismatch) return;
    setLoading(true);
    setError(null);
    try {
      await http.post(
        "/api/auth/reset-password",
        { token: token.trim(), password },
        { anonymous: true },
      );
      navigate("/login", { replace: true });
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "No se pudo cambiar la contraseña.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthShell
      kicker="Recuperación"
      title="Elegí una contraseña nueva"
      description="Pegá el código que te llegó por correo y elegí la contraseña nueva. Al cambiarla se cierran todas tus sesiones abiertas."
      footer={
        <span className="flex flex-wrap gap-x-4 gap-y-1">
          <Link to="/recuperar" className="transition-colors hover:text-ink">
            Pedir un código nuevo
          </Link>
          <Link to="/login" className="transition-colors hover:text-ink">
            Volver al login
          </Link>
        </span>
      }
    >
      <form onSubmit={(event) => void submit(event)} className="flex flex-col gap-4">
        <Input
          label="Código del email"
          required
          value={token}
          onChange={(event) => setToken(event.target.value)}
          autoFocus={!params.get("token")}
          autoComplete="one-time-code"
          hint="Copialo del correo «Restablecer tu contraseña». Vence en una hora y sirve una sola vez."
        />
        <Input
          label="Contraseña nueva"
          type="password"
          required
          minLength={8}
          icon={<KeyRound />}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          hint="Mínimo 8 caracteres."
        />
        <Input
          label="Repetila"
          type="password"
          required
          value={repeat}
          onChange={(event) => setRepeat(event.target.value)}
          error={mismatch ? "Las contraseñas no coinciden." : undefined}
        />
        {error ? (
          <p role="alert" className="text-[12px] text-danger-fg">
            {error}
          </p>
        ) : null}
        <Button type="submit" variant="primary" block loading={loading} disabled={mismatch}>
          Cambiar contraseña
        </Button>
      </form>
    </AuthShell>
  );
}

export default ResetPasswordPage;
